import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, FolderKanban } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useMe } from "@/lib/session";

export const Route = createFileRoute("/_app/")({
  component: DashboardPage,
});

interface ProjectCard {
  id: string;
  name: string;
  status: string;
  health: string;
  description: string | null;
  hasPlane: boolean;
  avatarR2Key: string | null;
  coverR2Key: string | null;
  categories: Array<{ id: string; name: string; color: string | null }>;
}

function DashboardPage() {
  const { data: me } = useMe();
  const projectsQ = useQuery<{ projects: ProjectCard[] }>({
    queryKey: ["projects"],
    queryFn: () => api.get("/projects"),
  });
  const projects = projectsQ.data?.projects ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Dashboard"
        title={`Hola, ${me?.user?.name?.split(" ")[0] ?? ""}`}
        subtitle="Aquí están tus proyectos con Axorial. Cada uno tiene su tablero de tickets, comentarios y archivos."
      />

      {projectsQ.isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : projects.length === 0 ? (
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          Aún no hay proyectos visibles. Si crees que es un error,
          contacta a tu lead en Axorial.
        </div>
      ) : (
        <>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-base-content/60">
            Tus proyectos · {projects.length}
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {projects.map((p) => (
              <li key={p.id}>
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: p.id }}
                  className="card-elevated block hover:[&]:card-elevated-hover h-full overflow-hidden"
                >
                  {p.coverR2Key ? (
                    <div className="aspect-[3/1] bg-base-200 overflow-hidden">
                      <img
                        src={`/api/projects/${p.id}/cover`}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[3/1] bg-gradient-to-br from-primary/20 to-secondary/20" />
                  )}
                  <div className="p-5 -mt-8 relative">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      {p.avatarR2Key ? (
                        <div className="size-12 rounded-lg overflow-hidden ring-4 ring-base-100 bg-base-100">
                          <img
                            src={`/api/projects/${p.id}/avatar`}
                            alt={p.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ) : (
                        <div className="size-12 rounded-lg bg-primary/15 text-primary grid place-items-center ring-4 ring-base-100">
                          <FolderKanban className="size-5" />
                        </div>
                      )}
                      <ArrowUpRight className="size-4 text-base-content/40 mt-9" />
                    </div>
                    <h3 className="font-display font-semibold leading-tight mb-1">
                      {p.name}
                    </h3>
                    {p.description && (
                      <p className="text-xs text-base-content/65 line-clamp-2">{p.description}</p>
                    )}
                    {p.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {p.categories.slice(0, 4).map((c) => (
                          <span
                            key={c.id}
                            className="badge badge-xs badge-outline"
                            style={c.color ? { borderColor: c.color, color: c.color } : undefined}
                          >
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
